import { useState, useEffect } from "react";
import { Modal, Form, Input, Space, Typography, Select, InputNumber, Button, Alert, Tooltip } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { ColorPickerWithLabel } from "../OverviewSettings/ColorPickerWithLabel";
import { getColorStrategyLockReason } from "@utils/color_utils";

const { Option } = Select;


export const ColorStrategyModal = ({
  isVisible,
  onCancel,
  onSubmit,
  editingStrategy = null,
  currentUserId,
  strategyVariables = []
}) => {
  const lockReason = editingStrategy ? getColorStrategyLockReason(editingStrategy, currentUserId) : null;
  const [selectedColors, setSelectedColors] = useState(['#000000', '#000000']);
  const [form] = Form.useForm();
  
  // Watch form values to conditionally show sds_from_mean dropdown
  const conditions = Form.useWatch('conditions', form) || [];

  // Build grouped option lists from the backend-provided strategyVariables
  const ungroupedVarOptions = [];
  const groupedVarOptions = {};
  strategyVariables.forEach(config => {
    if (!config) return;
    if (config.group) {
      if (!groupedVarOptions[config.group]) groupedVarOptions[config.group] = [];
      groupedVarOptions[config.group].push(config);
    } else {
      ungroupedVarOptions.push(config);
    }
  });

  useEffect(() => {
    if (editingStrategy) {
      try {
        const logicObj = typeof editingStrategy.logic === 'string' ? JSON.parse(editingStrategy.logic) : editingStrategy.logic;
        const ifArray = logicObj.if;
        
        // The last element is always the else color
        const elseColor = ifArray[ifArray.length - 1];
        
        // Process conditions (excluding the last element which is the else color)
        const conditions = [];
        for (let i = 0; i < ifArray.length - 1; i += 2) {
          const condition = ifArray[i];
          const color = ifArray[i + 1];
          
          // Extract operator (first key in the condition object)
          const operator = Object.keys(condition)[0];
          
          // Extract the comparison details
          const comparison = condition[operator][1];
          
          let compareType = 'constant';
          let sdsValue = 0;
          let multiplier = 1;
          
          // Check if this is an addition operation (hist_std_dev * multiplier + hist_avg)
          if (comparison?.["+"] && Array.isArray(comparison["+"])) {
            const additionArray = comparison["+"];
            // Look for the multiplication part and hist_avg part
            const multiplyPart = additionArray.find(part => part["*"]);
            const histAvgPart = additionArray.find(part => part.var === "hist_avg");
            
            if (multiplyPart && histAvgPart) {
              const varPath = multiplyPart["*"][0].var;
              if (varPath === 'hist_std_dev') {
                compareType = 'sds_from_mean'; // Frontend representation
                sdsValue = multiplyPart["*"][1];
              }
            }
          } else if (comparison?.["*"]) {
            // Handle regular multiplication operations
            multiplier = comparison["*"][1] || 1;
            const varPath = comparison["*"][0].var || 'one';
            
            if (varPath === 'metric_baseline') compareType = 'baseline';
            else {
              const varConfig = strategyVariables.find(v => v.key === varPath);
              if (varConfig) compareType = varConfig.compareType;
            }
          }
          
          conditions.push({
            operator,
            compareType,
            constantValue: compareType === 'constant' ? 1 : undefined,
            baselineValue: compareType === 'baseline' ? 'metric_baseline' : undefined,
            multiplier: compareType === 'sds_from_mean' ? 1 : multiplier, // Always 1 for sds_from_mean
            sdsValue,
            color
          });
        }

        // Set up form values
        form.setFieldsValue({
          name: editingStrategy.name,
          conditions,
          elseColor
        });

        // Update color state
        setSelectedColors([...conditions.map(c => c.color), elseColor]);
      } catch (error) {
        console.error("Error parsing strategy logic:", error);
        form.setFieldsValue({
          name: editingStrategy.name
        });
      }
    } else {
      form.resetFields();
      setSelectedColors(['#000000', '#000000']);
    }
  }, [editingStrategy, form, strategyVariables]);

  const handleColorChange = (index, _, color) => {
    const newColors = [...selectedColors];
    newColors[index] = color;
    setSelectedColors(newColors);
    if (index === selectedColors.length - 1) {
      // This is the else condition
      form.setFieldValue('elseColor', color);
    } else {
      const conditions = form.getFieldValue('conditions') || [];
      conditions[index] = { ...conditions[index], color };
      form.setFieldValue('conditions', conditions);
    }
  };

  const handleSubmit = (values) => {
    // Construct the JSON Logic for multiple conditions
    const conditions = values.conditions || [];
    const jsonLogic = {
      if: conditions.map(condition => [
        {
          [condition.operator]: [
            {"var": "value"},
            condition.compareType === 'sds_from_mean' ? {
              "+": [
                {
                  "*": [
                    {"var": "hist_std_dev"},
                    condition.sdsValue
                  ]
                },
                {"var": "hist_avg"}
              ]
            } : {
              "*": [
                {"var": condition.compareType === 'constant' ? 'one' : 
                        condition.compareType === 'baseline' ? 'metric_baseline' :
                        condition.compareType},
                condition.multiplier
              ]
            }
          ]
        },
        condition.color,
      ]).flat()
    };

    // Add else color as the last element
    jsonLogic.if.push(form.getFieldValue('elseColor') || "#000000");

    const strategyData = {
      name: values.name,
      logic: jsonLogic
    };

    onSubmit(strategyData, editingStrategy);
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedColors(['#000000', '#000000']);
    onCancel();
  };

  return (
    <Modal
      title={editingStrategy ? "Edit Strategy" : "Add Strategy"}
      open={isVisible}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      style={{ minWidth: '1000px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: '100%' }}
        initialValues={{
          conditions: [{ sdsValue: 0 }] // Start with one empty condition
        }}
      >
        <Form.Item
          name="name"
          label="Strategy Name"
          rules={[{ required: true, message: "Please enter a strategy name" }]}
        >
          <Input />
        </Form.Item>

        <Form.List name="conditions">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {fields.map((field, index) => (
                <Space key={field.key} size="small" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                  <Typography.Text strong>
                    {index === 0 ? <span style={{marginLeft: '30px'}}>If value</span> : <span>else if value</span>}
                  </Typography.Text>
                  
                  <Form.Item
                    {...field}
                    name={[field.name, 'operator']}
                    rules={[{ required: true, message: "Required" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select defaultValue="==" style={{ width: '100%' }}>
                      <Option value="==">==</Option>
                      <Option value=">">&gt;</Option>
                      <Option value=">=">&gt;=</Option>
                      <Option value="<">&lt;</Option>
                      <Option value="<=">&lt;=</Option>
                    </Select>
                  </Form.Item>

                  <span style={{fontSize: '20px'}}>(</span>

                  <Form.Item
                    {...field}
                    name={[field.name, 'compareType']}
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  > 
                    <Select style={{ width: '220px' }} placeholder="Select Variable">
                      {ungroupedVarOptions.map(cfg => (
                        <Option key={cfg.compareType} value={cfg.compareType}>{cfg.label}</Option>
                      ))}
                      <Option value="sds_from_mean">Avg + (SD * n)</Option>
                      {Object.entries(groupedVarOptions).map(([groupLabel, items]) => (
                        <Select.OptGroup key={groupLabel} label={groupLabel}>
                          {items.map(cfg => (
                            <Option key={cfg.compareType} value={cfg.compareType}>{cfg.label}</Option>
                          ))}
                        </Select.OptGroup>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  {/* Show sdsValue input for sds_from_mean, otherwise show multiplier */}
                  {conditions[index]?.compareType === 'sds_from_mean' ? (
                    <>
                      <Typography.Text>×</Typography.Text>
                      <Form.Item
                        {...field}
                        name={[field.name, 'sdsValue']}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber style={{ width: 100, borderRadius: '5px' }} placeholder="SDs" />
                      </Form.Item>
                      <span style={{fontSize: '20px'}}>)</span>
                    </>
                  ) : (
                    <>
                      <Typography.Text>×</Typography.Text>
                      <Form.Item
                        {...field}
                        name={[field.name, 'multiplier']}
                        rules={[{ required: true, message: "Required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber style={{ width: 100, borderRadius: '5px' }} placeholder="Multiplier" />
                      </Form.Item>
                      <span style={{fontSize: '20px'}}>)</span>
                    </>
                  )}

                  <Typography.Text strong>, then</Typography.Text>

                  <Form.Item
                    {...field}
                    name={[field.name, 'color']}
                    style={{ marginBottom: 0 }}
                  >
                    <ColorPickerWithLabel
                      pickerId={`strategyColor${index}`}
                      defaultColor={selectedColors[index] || '#000000'}
                      handleColorChangeParent={(_, color) => handleColorChange(index, _, color)}
                    />
                  </Form.Item>

                  {index > 0 && (
                    <MinusCircleOutlined onClick={() => {
                      remove(field.name);
                      const newColors = [...selectedColors];
                      newColors.splice(index, 1);
                      setSelectedColors(newColors);
                    }} />
                  )}
                </Space>
              ))}

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="dashed"
                  onClick={() => {
                    add({ sdsValue: 0 });
                    setSelectedColors([...selectedColors.slice(0, -1), '#000000', selectedColors[selectedColors.length - 1]]);
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Condition
                </Button>
              </Form.Item>

              {/* Permanent else condition */}
              <Space size="small" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <Typography.Text strong>else</Typography.Text>
                
                <Form.Item
                  name="elseColor"
                  style={{ marginBottom: 0 }}
                  initialValue="#000000"
                >
                  <ColorPickerWithLabel
                    pickerId="elseColor"
                    defaultColor={selectedColors[selectedColors.length - 1]}
                    handleColorChangeParent={(_, color) => handleColorChange(selectedColors.length - 1, _, color)}
                  />
                </Form.Item>
              </Space>
            </Space>
          )}
        </Form.List>

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Tooltip title={lockReason}>
              <Button type="primary" htmlType="submit" disabled={!!lockReason}>
                {editingStrategy ? "Update" : "Create"}
              </Button>
            </Tooltip>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}; 